import React, { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { CalendarPlus, Copy, Loader2, ExternalLink, Link2Off } from "lucide-react";
import { toast } from "sonner";

export function CalendarSync() {
  const [open, setOpen] = useState(false);
  const [url, setUrl] = useState("");
  const [active, setActive] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const show = async () => {
    setOpen(true); setBusy(true); setError("");
    try { setActive((await api.get("/calendar/subscription")).data.active); }
    catch (e) { setError(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const create = async () => {
    if (active && !window.confirm("Je oude agendalink wordt ongeldig. Voeg de nieuwe link daarna opnieuw toe in Google of Apple Agenda. Doorgaan?")) return;
    setBusy(true); setError("");
    try {
      const { data } = await api[active ? "put" : "post"]("/calendar/subscription");
      setUrl(data.url); setActive(true);
    } catch (e) { setError(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const revoke = async () => {
    if (!window.confirm("Agendasynchronisatie stoppen? Verwijder ook het abonnement in Google of Apple Agenda.")) return;
    setBusy(true); setError("");
    try { await api.delete("/calendar/subscription"); setActive(false); setUrl(""); toast.success("Agendalink ingetrokken"); }
    catch (e) { setError(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const copy = async () => {
    try { await navigator.clipboard.writeText(url); toast.success("Persoonlijke agendalink gekopieerd"); }
    catch (_) { setError("Selecteer en kopieer de link handmatig; je browser blokkeert het klembord."); }
  };
  return <>
    <Button variant="outline" onClick={show} data-testid="calendar-sync-button"><CalendarPlus className="w-4 h-4 mr-2" />Agenda synchroniseren</Button>
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-lg bg-white dark:bg-slate-900 max-h-[90vh] overflow-y-auto" data-testid="calendar-sync-dialog">
        <DialogHeader><DialogTitle>Jouw diensten in je eigen agenda</DialogTitle><DialogDescription>Abonneer je op je gepubliceerde diensten. Alleen-lezen: wijzigingen maak je in deze app.</DialogDescription></DialogHeader>
        <p className="text-xs rounded-lg bg-amber-50 text-amber-900 border border-amber-200 p-3" data-testid="calendar-sync-privacy">Houd je persoonlijke link geheim. Iedereen met deze link kan je dienstnamen, tijden en locaties bekijken.</p>
        {error && <p role="alert" className="text-sm text-rose-700" data-testid="calendar-sync-error">{error}</p>}
        {active && !url && <p className="text-sm" data-testid="calendar-sync-active">Je abonnement is actief. De geheime link wordt alleen bij aanmaken getoond. Vernieuw hem alleen als je opnieuw wilt koppelen.</p>}
        {url ? <div className="space-y-3">
          <label htmlFor="calendar-subscription-url" className="text-sm font-medium">Persoonlijke abonnementlink</label>
          <div className="flex gap-2"><Input id="calendar-subscription-url" readOnly value={url} onFocus={(e) => e.target.select()} data-testid="calendar-subscription-url" /><Button variant="outline" size="icon" onClick={copy} aria-label="Agendalink kopiëren" data-testid="calendar-sync-copy"><Copy className="w-4 h-4" /></Button></div>
          <div className="flex flex-wrap gap-2">
            <Button asChild><a href={`https://calendar.google.com/calendar/u/0/r?cid=${encodeURIComponent(url)}`} target="_blank" rel="noopener noreferrer" data-testid="calendar-sync-google"><ExternalLink className="w-4 h-4 mr-2" />Google Agenda</a></Button>
            <Button variant="outline" asChild><a href={url.replace(/^https?:/, "webcal:")} data-testid="calendar-sync-apple"><CalendarPlus className="w-4 h-4 mr-2" />Apple Agenda</a></Button>
          </div>
        </div> : <Button onClick={create} disabled={busy} data-testid="calendar-sync-create">{busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}{active ? "Nieuwe agendalink maken" : "Persoonlijke agendalink maken"}</Button>}
        <div className="space-y-2 text-sm text-muted-foreground" data-testid="calendar-sync-instructions">
          <p><strong>Google:</strong> gebruik een computerbrowser. Werkt de knop niet? Kies ‘Andere agenda’s’ → ‘+’ → ‘Via URL’ en plak de link.</p>
          <p><strong>Apple:</strong> open de link op je iPhone of Mac. Of kies in Agenda ‘Voeg agenda toe’ → ‘Voeg agenda-abonnement toe’ en plak de link.</p>
          <p>Google en Apple bepalen wanneer ze wijzigingen ophalen. Dit kan enkele uren duren; synchronisatie is niet direct.</p>
        </div>
        {active && <Button variant="ghost" className="text-rose-700 justify-start" onClick={revoke} disabled={busy} data-testid="calendar-sync-revoke"><Link2Off className="w-4 h-4 mr-2" />Synchronisatie intrekken</Button>}
      </DialogContent>
    </Dialog>
  </>;
}
